import { createStyles, withStyles, type WithStyles } from "@material-ui/core";
import { filterLotPredicate } from "features/lots/utils/lots.utils";
import { CustomTable } from "features/shared/components";
import React, {
  type MutableRefObject,
  useEffect,
  useImperativeHandle,
} from "react";
import arrayUtils from "shared/arrayUtils";
import type {
  EnrichedAnimalCategory,
  Goal,
  LivestockInfoFromFarm,
} from "shared/types";
import { useLotListTable } from "../../hooks/useLotListTable";
import { useLotTableForm } from "../../hooks/useLotTableForm";
import type {
  LotsFilterFormState,
  LotsFormRef,
  LotsPermissions,
} from "../../lots.types";
import EmptyLotsTable from "./components/emptyLotsTable";

type LotsListTableProps = {
  enrichedAnimalCategories: EnrichedAnimalCategory[];
  filterForm: LotsFilterFormState;
  formRef: MutableRefObject<LotsFormRef | null>;
  isFilterApplied: boolean;
  livestockInfosFromFarm: LivestockInfoFromFarm[];
  lotGoals: Goal[];
  lotsPermissions: LotsPermissions;
  onCanSubmitFormChange: (canSubmit: boolean) => void;
  onOpenWeightProjectionDetails: (row: LivestockInfoFromFarm) => void;
} & WithStyles<typeof styles>;

function LotsListTable({
  classes,
  enrichedAnimalCategories,
  filterForm,
  formRef,
  isFilterApplied,
  livestockInfosFromFarm,
  lotGoals,
  lotsPermissions,
  onCanSubmitFormChange,
  onOpenWeightProjectionDetails,
}: LotsListTableProps) {
  const {
    canSubmitForm,
    handleBlurRowField,
    handleRowFieldChange,
    handleRowFieldError,
    tableChangeErrors,
    tableChanges,
  } = useLotTableForm();

  useImperativeHandle(
    formRef,
    () => ({
      getTableChanges: () => tableChanges,
    }),
    [tableChanges]
  );

  useEffect(() => {
    onCanSubmitFormChange(canSubmitForm);
  }, [canSubmitForm, onCanSubmitFormChange]);

  const { columns } = useLotListTable({
    enrichedAnimalCategories,
    lotGoals: lotGoals,
    lotsPermissions,
    onBlurRowField: handleBlurRowField,
    onOpenWeightProjectionDetails,
    onRowFieldChange: handleRowFieldChange,
    onRowFieldError: handleRowFieldError,
    tableChangeErrors,
    tableChanges,
  });

  const filteredRows = arrayUtils.filterItems({
    items: livestockInfosFromFarm,
    predicate: (item) => filterLotPredicate(item, filterForm),
  });

  return (
    <CustomTable
      columns={columns}
      getRowId={(row) => row.id.toString()}
      id="lots-list-table"
      pagination={true}
      rows={filteredRows}
      renderEmptyTable={() => (
        <EmptyLotsTable isFilterApplied={isFilterApplied} />
      )}
      className={{
        bodyCell: classes.bodyCell,
        headerCell: classes.headerCell,
        paper: classes.paper,
        root: classes.root,
      }}
    />
  );
}

const styles = () =>
  createStyles({
    paper: {
      "@media print": {
        maxHeight: "none !important",
        overflowX: "hidden",
        overflowY: "visible",
      },
    },
    root: {
      "@media print": {
        maxHeight: "none !important",
        overflowX: "hidden",
        overflowY: "visible",
      },
    },
    headerCell: {
      "@media print": {
        fontSize: "10px !important",
        padding: "0 12px !important",
        width: "min-content !important",
      },
    },
    bodyCell: {
      "@media print": {
        fontSize: "10px !important",
        padding: "0 12px !important",
        width: "min-content !important",
      },
    },
  });

export default withStyles(styles)(LotsListTable);
